import React, { useEffect, useState } from "react";
import Header from "../../Layout/Header";
import AssessmentBanner from "./AssessmentBanner"; // Adjust the path as needed
import AssessmentNavigation from "./AssessmentNavigation";
import {
  useAssessmentRunSummary,
  useAssessments,
  useFeedbackOptions,
  useUpdateAssessment,
} from "./assessment_hooks";
import { CommonPageData } from "../../../styles/common.style";
import { useDispatch } from "react-redux";
import { setAlert } from "../../../actions/alert";
import VideoPlayer from "./Components/VideoPlayer";
import FileInput from "./Components/FileInput";
import UploadedFilePreview from "./Components/UploadedFilePreview";
import SelectedFilePreview from "./Components/SelectedFilePreview";
import FeedbackSection from "./Components/FeedbackSection";
import Loading from "./Components/Loading";
import ResetModal from "./Components/ResetModal";
import AnalysisErrorModal from "./Components/AnalysisErrorModal";
import FinishModal from "./Components/FinishModal";
import FinalizeMissingItemsModal from "./Components/FinalizeMissingItemsModal";

function PoseNote({ note }) {
  return <h3>{note}</h3>;
}

function Layouts({ isFeedback, children }) {
  let videoPlayer,
    uploadedFilePreview,
    fileInput,
    selectedFilePreview,
    feedbackSection,
    assessmentNavigation,
    poseNote;

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      // Check if the child is a valid React element
      if (child.type === VideoPlayer) {
        videoPlayer = child;
      } else if (child.type === UploadedFilePreview) {
        uploadedFilePreview = child;
      } else if (child.type === FileInput) {
        fileInput = child;
      } else if (child.type === SelectedFilePreview) {
        selectedFilePreview = child;
      } else if (child.type === FeedbackSection) {
        feedbackSection = child;
      } else if (child.type === AssessmentNavigation) {
        assessmentNavigation = child;
      } else if (child.type === PoseNote) {
        poseNote = child;
      }
    }
  });

  if (isFeedback) {
    return (
      <section className="test1_001 assessment-block">
        <div className="container">
          <div className="row">
            <div className="col-12 col-lg-6 col-md-6">
              <div className="ssa box_video">{videoPlayer}</div>
              {uploadedFilePreview}
              {fileInput}
              {selectedFilePreview}
              {poseNote}
            </div>

            <div className="col-12 col-lg-6 col-md-6 test_one">
              {feedbackSection}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="assessment-block">
      <div className="container">
        <div className="col-12 col-lg-12 col-md-12">
          <div className="assessment-block-inner">
            {videoPlayer}
            {uploadedFilePreview}
            {fileInput}
            {selectedFilePreview}
            {poseNote}
            {assessmentNavigation}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Assessment() {
  const [currentAssessmentIndex, setCurrentAssessmentIndex] = useState(0);
  const assessments = useAssessments();

  const {
    processingStatus,
    updateAssessment,
    finishAssessment,
    fileUploadPercentage,
    resetProcessingStatus,
    analysisError,
    clearAnalysisError,
  } = useUpdateAssessment();

  const questions = useFeedbackOptions(assessments, currentAssessmentIndex);

  const dispatch = useDispatch();

  // TODO: Move to custom hook
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const [note, setNote] = useState("");
  // null = untouched (render from previouslySelectedOptions);
  // [] = edited but currently empty (do NOT re-seed).
  const [selectedOptions, setSelectedOptions] = useState(null);
  const [showFeedbackError, setShowFeedbackError] = useState(false);

  function handleBack() {
    if (currentAssessmentIndex > 0) {
      setCurrentAssessmentIndex(currentAssessmentIndex - 1);
      clearInput();
    }
  }

  function handleFileChange(file) {
    if (file) {
      setFile(file);
      setPreview(URL.createObjectURL(file));
    }
  }

  const { assessmentRunSummary, fetchAssessmentData } =
    useAssessmentRunSummary(); // Fetch assessment run summary if needed
  const [previousUploadUrl, setPreviousUploadUrl] = useState(null);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [previouslySelectedOptions, setPreviouslySelectedOptions] = useState(
    [],
  );
  const [previousNote, setPreviousNote] = useState("");

  // Sync previously uploaded assessment data
  // This handles when customer uses back button and goes to already finished assessment
  useEffect(() => {
    if (
      assessmentRunSummary &&
      assessments.length > 0 &&
      currentAssessmentIndex !== null
    ) {
      const currentAssessment = assessments[currentAssessmentIndex];
      const url = assessmentRunSummary[currentAssessment.id]?.url || null;

      const prevOptions =
        assessmentRunSummary[currentAssessment.id]?.userFeedbackOptions?.map(
          ({ feedbackOption }) => feedbackOption,
        ) || [];

      const prevNote = assessmentRunSummary[currentAssessment.id]?.notes || "";

      if (url) {
        setPreviousUploadUrl(url);
        setPreviouslySelectedOptions(prevOptions);
        setPreviousNote(prevNote);
      } else {
        setPreviousUploadUrl(null);
        setPreviouslySelectedOptions([]);
      }
    }
  }, [assessmentRunSummary, assessments, currentAssessmentIndex]);

  // Navigate to the assessment which is not done yet
  // If all assessments are already done, show a reset modal
  useEffect(() => {
    if (
      assessments.length > 0 &&
      assessmentRunSummary &&
      currentAssessmentIndex === 0
    ) {
      let assessmentIndex = 0;

      if (Object.keys(assessmentRunSummary).length === 0) {
        // If no assessments have been run, start from the first assessment
        return;
      }

      while (
        assessments[assessmentIndex]?.file_type === "none" ||
        assessmentRunSummary[assessments[assessmentIndex]?.id]
      ) {
        assessmentIndex++;
      }

      if (assessmentIndex < assessments.length) {
        setCurrentAssessmentIndex(assessmentIndex);
      } else {
        setCurrentAssessmentIndex(assessments.length - 1); // Reset to the last assessment if no valid index found
        setIsResetModalOpen(true); // Open reset modal if no valid assessment found
      }
    }
  }, [assessmentRunSummary, assessments, currentAssessmentIndex]);

  function clearInput() {
    setFile(null); // Reset file selection when moving to next assessment
    setPreview(null); // Reset preview when moving to next assessment
    setNote(""); // Reset note when moving to next assessment
    setSelectedOptions(null); // Reset selected options when moving to next assessment
    setShowFeedbackError(false);

    setPreviousUploadUrl(null);
    setPreviousNote("");
    setPreviouslySelectedOptions([]);
  }

  function handleNextAssessment() {
    if (currentAssessmentIndex < assessments.length - 1) {
      setCurrentAssessmentIndex(currentAssessmentIndex + 1);
      clearInput();
    }
  }

  const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
  const [isFinalizeMissingItemsModalOpen, setIsFinalizeMissingItemsModalOpen] =
    useState(false);
  const [finalizeMissingItems, setFinalizeMissingItems] = useState([]);
  const [finalizeErrorMessage, setFinalizeErrorMessage] = useState("");

  function normalizeFinalizeMissingItems(errorPayload) {
    const fromPendingAssessments = Array.isArray(
      errorPayload?.data?.pendingAssessments,
    )
      ? errorPayload.data.pendingAssessments
      : [];

    if (fromPendingAssessments.length > 0) {
      return fromPendingAssessments.map((item) => ({
        assessmentId: Number.isFinite(Number(item?.assessmentId))
          ? Number(item.assessmentId)
          : null,
        slug: item?.slug || null,
        name: item?.name || "",
      }));
    }

    const fromMissingAssessments = Array.isArray(errorPayload?.missingAssessments)
      ? errorPayload.missingAssessments
      : [];

    return fromMissingAssessments.map((item) => ({
      assessmentId: Number.isFinite(Number(item?.id)) ? Number(item.id) : null,
      slug: item?.slug || null,
      name: item?.title || item?.name || "",
    }));
  }

  function getAssessmentIndexByMissingItem(item) {
    if (!Array.isArray(assessments) || assessments.length === 0 || !item) return -1;

    if (item.assessmentId !== null && item.assessmentId !== undefined) {
      const byId = assessments.findIndex(
        (assessment) => Number(assessment?.id) === Number(item.assessmentId),
      );
      if (byId >= 0) return byId;
    }

    if (item.slug) {
      const bySlug = assessments.findIndex(
        (assessment) => (assessment?.slug || "").trim() === item.slug.trim(),
      );
      if (bySlug >= 0) return bySlug;
    }

    if (item.name) {
      const targetName = item.name.trim().toLowerCase();
      const byName = assessments.findIndex(
        (assessment) =>
          String(assessment?.title || "")
            .trim()
            .toLowerCase() === targetName,
      );
      if (byName >= 0) return byName;
    }

    return -1;
  }

  function navigateToMissingAssessment(item) {
    const targetIndex = getAssessmentIndexByMissingItem(item);

    if (targetIndex < 0) {
      dispatch(
        setAlert(
          "Could not locate the missing assessment page. Please select it manually.",
          "danger",
        ),
      );
      return;
    }

    setCurrentAssessmentIndex(targetIndex);
    clearInput();
    setIsFinishModalOpen(false);
    setFinalizeMissingItems([]);
    setFinalizeErrorMessage("");
    setIsFinalizeMissingItemsModalOpen(false);
  }

  function closeFinalizeMissingItemsModal() {
    setIsFinalizeMissingItemsModalOpen(false);
    setFinalizeMissingItems([]);
    setFinalizeErrorMessage("");
  }

  async function handleFinishAssessment() {
    // Logic to finalize the assessment
    try {
      setIsFinishModalOpen(true);
      await finishAssessment();
    } catch (error) {
      const missingItems = normalizeFinalizeMissingItems(error);
      if (missingItems.length > 0) {
        setFinalizeMissingItems(missingItems);
        setFinalizeErrorMessage(
          error?.message ||
            "Cannot finalize assessment. Please complete the missing items below.",
        );
        setIsFinalizeMissingItemsModalOpen(true);
        setIsFinishModalOpen(false);
        return;
      }

      dispatch(
        setAlert(
          error.message || "Failed to finalize assessment. Please try again.",
          "danger",
        ),
      );
      setIsFinishModalOpen(false);
    }
  }

  async function handleNext() {
    const hasNewChange = file || note || (selectedOptions?.length ?? 0) > 0;
    const hasPreviousData =
      previousUploadUrl || previousNote || previouslySelectedOptions.length > 0;
    const isAssessmentFinalStep =
      currentAssessmentIndex >= assessments.length - 1;

    // Check if there is any change
    // If no change check if there was previous assessment data
    if (!hasNewChange && hasPreviousData) {
      // No changes made, check for previous assessment data
      if (isAssessmentFinalStep) {
        await handleFinishAssessment();
      } else {
        handleNextAssessment();
      }
      return;
    }

    let isError = false;

    // Multi-select questions can contribute multiple entries per question,
    // so check per-question presence rather than total selection count.
    const isQuestionAnswered = (question) =>
      (selectedOptions ?? []).some((opt) => opt.parent_id === question.id) ||
      previouslySelectedOptions.some((opt) => opt.parent_id === question.id);

    if (!questions.every(isQuestionAnswered)) {
      setShowFeedbackError(true);
      return;
    } else {
      setShowFeedbackError(false);
    }

    if (assessments[currentAssessmentIndex]?.file_type !== "none") {
      if (!file && !previousUploadUrl) {
        dispatch(setAlert("Please upload a file before proceeding.", "danger"));
        return;
      }

      try {
        await updateAssessment(
          file,
          assessments[currentAssessmentIndex],
          note,
          selectedOptions ?? [],
          previousUploadUrl,
        );

        // TODO: This check will be removed along with <resetAssessmentModal>
        if (!isAssessmentFinalStep) {
          await fetchAssessmentData();
        }
      } catch (error) {
        dispatch(
          setAlert(
            error?.message || "Assessment failed. Please try again.",
            "danger",
          ),
        );
        isError = true;
      } finally {
        resetProcessingStatus(); // Reset processing status after update
      }
    }

    if (isAssessmentFinalStep) {
      // If it's the last assessment, finalize it
      await handleFinishAssessment();
      return;
    }

    if (!isError) {
      handleNextAssessment();
    }
  }

  return (
    <div>
      <Header />

      <CommonPageData>
        <Loading
          processingStatus={processingStatus}
          fileUploadPercentage={fileUploadPercentage}
        />

        <AssessmentBanner
          title={assessments[currentAssessmentIndex]?.title}
          currentIndex={currentAssessmentIndex}
          totalAssessments={assessments.length}
        />

        <Layouts
          isFeedback={
            assessments[currentAssessmentIndex]?.assessmentFeedbackOption
              ?.length > 0
          }
        >
          <VideoPlayer url={assessments[currentAssessmentIndex]?.video} />
          <UploadedFilePreview
            fileType={assessments[currentAssessmentIndex]?.file_type}
            url={previousUploadUrl}
          />

          <FileInput
            fileType={assessments[currentAssessmentIndex]?.file_type}
            selectedFile={file}
            hasExistingUpload={!!previousUploadUrl}
            onSelect={handleFileChange}
          />

          <PoseNote note={assessments[currentAssessmentIndex]?.note || ""} />

          <SelectedFilePreview
            fileType={assessments[currentAssessmentIndex]?.file_type}
            url={preview}
          />

          <FeedbackSection
            questions={questions}
            selectedOptions={selectedOptions}
            previouslySelectedOptions={previouslySelectedOptions}
            onSelect={setSelectedOptions}
            note={note}
            previousNote={previousNote}
            onNoteChange={(e) => setNote(e.target.value)}
            isValidationError={showFeedbackError}
          >
            <AssessmentNavigation
              currentIndex={currentAssessmentIndex}
              totalAssessments={assessments.length}
              isLoading={false}
              onBack={handleBack}
              onNext={handleNext}
            />
          </FeedbackSection>

          <AssessmentNavigation
            currentIndex={currentAssessmentIndex}
            totalAssessments={assessments.length}
            isLoading={false}
            onBack={handleBack}
            onNext={handleNext}
          />
        </Layouts>
      </CommonPageData>

      <ResetModal isOpen={isResetModalOpen} onClose={() => setIsResetModalOpen(false)} />

      <AnalysisErrorModal error={analysisError} onClose={clearAnalysisError} />
      <FinishModal
        isOpen={isFinishModalOpen}
        onClose={() => setIsFinishModalOpen(false)}
      />
      <FinalizeMissingItemsModal
        isOpen={isFinalizeMissingItemsModalOpen}
        onClose={closeFinalizeMissingItemsModal}
        items={finalizeMissingItems}
        message={finalizeErrorMessage}
        onSelectItem={navigateToMissingAssessment}
        onSelectFirst={() => navigateToMissingAssessment(finalizeMissingItems[0])}
      />
    </div>
  );
}
